// Adventures of Black Emigo — WebAudio SFX + synthesized bassline
// Lazy AudioContext (must be unlocked by a user gesture).

let ctx = null;
let muted = false;
let masterGain = null;

function ensureCtx() {
  if (typeof window === "undefined") return null;
  if (!ctx) {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) return null;
    ctx = new Ctor();
    masterGain = ctx.createGain();
    masterGain.gain.value = 0.5;
    masterGain.connect(ctx.destination);
  }
  if (ctx.state === "suspended") {
    // Resume may reject if no user gesture has occurred yet. We'll retry on
    // the next gesture, so swallow the rejection without log spam.
    ctx.resume().catch(() => {});
  }
  return ctx;
}

export function unlockAudio() {
  ensureCtx();
}

export function setMuted(v) {
  muted = !!v;
  if (masterGain) masterGain.gain.value = muted ? 0 : 0.5;
}
export function isMuted() {
  return muted;
}

function tone({ freq = 440, type = "square", duration = 0.1, gain = 0.2, slideTo = null, attack = 0.005, release = 0.05 }) {
  const c = ensureCtx();
  if (!c || muted) return;
  const now = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, now);
  if (slideTo != null) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(40, slideTo), now + duration);
  }
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(gain, now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + duration + release);
  osc.connect(g);
  g.connect(masterGain);
  osc.start(now);
  osc.stop(now + duration + release + 0.02);
}

function noiseBurst({ duration = 0.12, gain = 0.18, filterFreq = 1200 }) {
  const c = ensureCtx();
  if (!c || muted) return;
  const now = c.currentTime;
  const bufferSize = Math.floor(c.sampleRate * duration);
  const buf = c.createBuffer(1, bufferSize, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
  }
  const src = c.createBufferSource();
  src.buffer = buf;
  const filter = c.createBiquadFilter();
  filter.type = "lowpass";
  filter.frequency.value = filterFreq;
  const g = c.createGain();
  g.gain.value = gain;
  src.connect(filter);
  filter.connect(g);
  g.connect(masterGain);
  src.start(now);
  src.stop(now + duration + 0.02);
}

// ----- SFX -----
export const sfx = {
  jump() {
    tone({ freq: 320, slideTo: 720, type: "square", duration: 0.14, gain: 0.18 });
  },
  doubleJump() {
    tone({ freq: 520, slideTo: 1040, type: "triangle", duration: 0.18, gain: 0.18 });
    tone({ freq: 880, slideTo: 1320, type: "square", duration: 0.1, gain: 0.1 });
  },
  coin() {
    tone({ freq: 988, type: "square", duration: 0.05, gain: 0.14 });
    setTimeout(() => tone({ freq: 1318, type: "square", duration: 0.1, gain: 0.16 }), 50);
  },
  stomp() {
    tone({ freq: 220, slideTo: 80, type: "square", duration: 0.16, gain: 0.22 });
    noiseBurst({ duration: 0.08, gain: 0.12, filterFreq: 900 });
  },
  hurt() {
    tone({ freq: 180, slideTo: 60, type: "sawtooth", duration: 0.32, gain: 0.22 });
    noiseBurst({ duration: 0.18, gain: 0.18, filterFreq: 600 });
  },
  powerup() {
    [523, 659, 784, 988].forEach((f, i) =>
      setTimeout(() => tone({ freq: f, type: "square", duration: 0.09, gain: 0.16 }), i * 55)
    );
  },
  bossHit() {
    tone({ freq: 140, slideTo: 60, type: "sawtooth", duration: 0.18, gain: 0.24 });
    noiseBurst({ duration: 0.12, gain: 0.15, filterFreq: 500 });
  },
  bossDefeat() {
    [196, 247, 311, 392, 494].forEach((f, i) =>
      setTimeout(() => tone({ freq: f, type: "square", duration: 0.12, gain: 0.2 }), i * 70)
    );
    setTimeout(() => noiseBurst({ duration: 0.4, gain: 0.18, filterFreq: 1800 }), 60);
  },
  achievement() {
    [659, 784, 1047, 1319].forEach((f, i) =>
      setTimeout(() => tone({ freq: f, type: "square", duration: 0.11, gain: 0.18 }), i * 75)
    );
  },
  gameOver() {
    [392, 349, 311, 261, 220].forEach((f, i) =>
      setTimeout(() => tone({ freq: f, type: "triangle", duration: 0.22, gain: 0.22 }), i * 130)
    );
  },
};

export function playSound(name) {
  const fn = sfx[name];
  if (typeof fn === "function") fn();
}

// ----- Synthesized bassline -----
// 8-step pattern @ ~96 BPM in eighth notes. Hip-hop minor groove.
const BASS_PATTERN = [
  { freq: 73,  gain: 0.18 }, // D2
  { freq: 73,  gain: 0.10 },
  { freq: 0,   gain: 0 },
  { freq: 73,  gain: 0.14 },
  { freq: 87,  gain: 0.18 }, // F2
  { freq: 0,   gain: 0 },
  { freq: 98,  gain: 0.18 }, // G2
  { freq: 65,  gain: 0.16 }, // C2
];

let musicOn = false;
let musicTimer = null;
let beatIndex = 0;
let beatListeners = new Set();
let bpm = 96;

function tickBass() {
  if (!musicOn) return;
  const step = BASS_PATTERN[beatIndex % BASS_PATTERN.length];
  if (step.freq > 0) {
    tone({ freq: step.freq, type: "sawtooth", duration: 0.22, gain: step.gain * 0.55, attack: 0.005, release: 0.06 });
    tone({ freq: step.freq * 2, type: "square", duration: 0.07, gain: step.gain * 0.12 });
  }
  // notify visualizer subscribers — isolate each so a broken one can't kill
  // the loop. Log unexpected errors in dev but continue scheduling.
  beatListeners.forEach((fn) => {
    try {
      fn(beatIndex, step);
    } catch (e) {
      if (
        typeof process !== "undefined" &&
        process.env &&
        process.env.NODE_ENV !== "production"
      ) {
        console.error("sfx: beat subscriber threw", e);
      }
    }
  });
  beatIndex++;
  // Step length = eighth note
  const stepMs = (60 / bpm) * 1000 * 0.5;
  musicTimer = setTimeout(tickBass, stepMs);
}

export function startMusic() {
  if (musicOn) return;
  ensureCtx();
  musicOn = true;
  beatIndex = 0;
  tickBass();
}

export function stopMusic() {
  musicOn = false;
  if (musicTimer) {
    clearTimeout(musicTimer);
    musicTimer = null;
  }
}

export function isMusicOn() {
  return musicOn;
}

export function setBpm(next) {
  bpm = Math.max(40, Math.min(220, next));
}

export function subscribeBeat(fn) {
  beatListeners.add(fn);
  return () => beatListeners.delete(fn);
}
